import {
  Breadcrumb,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Modal,
  Select,
  Tabs,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import AccountService from "../../../../services/accounts.service";
import moment from "moment";
import { amountFormat } from "../../../../services/common.service";
import { ReactComponent as TitleIcon } from "../../../../assests/images/accountBread.svg";
import { useTranslation } from "react-i18next";
import PendingAuthService from "../../../../services/approvals.service";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { modeSelector } from "../../../../store/selectors/app.selector";
import { useSelector } from "react-redux";
import LoanStatementGrid from "./loan-statement-grid";
import LoanStatementList from "./loanstatement-list";

export default function DetailedStatement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dataSource, setDataSource] = useState([]);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState("ACCOUNT_DETAILS");
  const [accountDetails, setAccountDetails] = useState({});
  const formValues = { dateType: "CURRENT", txnsTypes: "ALL" };
  const [formDetails, setFormDetails] = useState({});
  const [range, setRange] = useState("CURRENT");
  const [pwdSuccessVisible, setPwdSuccessVisible] = useState(false);
  const mode = useSelector(modeSelector);
  const [showGridAccount, setShowGridAccount] = useState("GRID");

  useEffect(() => {
    if (window.innerWidth < 900) {
      setShowGridAccount("LIST");
    }
  }, [window.innerWidth]);

  const txnTypes = [
    {
      label: t("pages.sendMoney.common_sendMoney.all", {
        defaultValue: "All",
      }),
      value: "ALL",
    },
    {
      label: t("pages.my_accounts.common_my_accounts.debit", {
        defaultValue: "Debit",
      }),
      value: "D",
    },
    {
      label: t("pages.my_accounts.common_my_accounts.credit", {
        defaultValue: "Credit",
      }),
      value: "C",
    },
  ];

  const PeriodTypes = [
    {
      label: t("pages.my_accounts.casa.current", {
        defaultValue: "Current",
      }),
      value: "CURRENT",
    },
    {
      label: t("pages.my_accounts.casa.three_months", {
        defaultValue: "3 Months",
      }),
      value: "3_MONTHS",
    },
    {
      label: t("pages.my_accounts.casa.six_months", {
        defaultValue: "6 Months",
      }),
      value: "6_MONTHS",
    },
    {
      label: t("pages.my_accounts.common_my_accounts.date_range", {
        defaultValue: "Date Range",
      }),
      value: "CUSTOM",
    },
  ];

  useEffect(() => {
    if (location.pathname === "/accounts/loan/statement") {
      setActiveKey("DETAILED_STATEMENT");
    }
    if (location.state) {
      setAccountDetails(location.state);
      getLoanStatement({
        ...formValues,
        ...location.state,
      });
    }
    setRange("CURRENT");
  }, []);

  const handleTabClick = (key) => {
    setActiveKey(key);
    if (key === "ACCOUNT_DETAILS") {
      navigate("/accounts/loan/summary", { state: accountDetails });
    }
    if (key === "DISBURSEMENT_INQUIRY") {
      navigate("/accounts/loan/disbursement-inquiry", {
        state: accountDetails,
      });
    }
    if (key === "SCHEDULE_INQUIRY") {
      navigate("/accounts/loan/schedule-inquiry", {
        state: accountDetails,
      });
    }
    if (key === "REPAYMENT") {
      navigate("/accounts/loan/repayment", {
        state: accountDetails,
      });
    }
  };

  const getLoanStatement = useCallback(
    async (values) => {
      try {
        let fromDate = "";
        let toDate = "";
        if (values.dateType === "CURRENT") {
          fromDate = moment().startOf("month").format("YYYY-MM-DD");
          toDate = moment().format("YYYY-MM-DD");
        }
        if (values.dateType === "3_MONTHS") {
          fromDate = moment().subtract(3, "month").format("YYYY-MM-DD");
          toDate = moment().format("YYYY-MM-DD");
        }
        if (values.dateType === "6_MONTHS") {
          fromDate = moment().subtract(6, "month").format("YYYY-MM-DD");
          toDate = moment().format("YYYY-MM-DD");
        }
        if (values.dateType === "CUSTOM") {
          if (values.dateRange !== undefined) {
            fromDate = values.dateRange[0].format("YYYY-MM-DD");
            toDate = values.dateRange[1].format("YYYY-MM-DD");
          }
        }
        setRange(values.dateType);
        const reqParams = {
          accountNo: values.accountNo || accountDetails.accountNo,
          branchId: values.branchId || accountDetails.branchId,
          fromDate: fromDate,
          toDate: toDate,
          txnsTypes: values.txnsTypes,
        };
        setFormDetails({ ...reqParams, ...values });
        const response = await AccountService.getLoanStatement(reqParams);
        if (response.status === 200) {
          setDataSource(response.data["txnDetails"]);
        }
      } catch (error) {}
    },
    [accountDetails]
  );

  const handleClear = () => {
    form.setFieldsValue({
      dateType: "CURRENT",
      txnsTypes: "ALL",
    });
    getLoanStatement({
      dateType: "CURRENT",
      txnsTypes: "ALL",
    });
  };

  const handleDownload = useCallback(async () => {
    try {
      let fromDate = "";
      let toDate = "";

      if (range === "CURRENT") {
        fromDate = moment().startOf("month");
        toDate = moment();
      }

      if (range === "3_MONTHS") {
        fromDate = moment().subtract(3, "month");
        toDate = moment();
      }
      if (range === "6_MONTHS") {
        fromDate = moment().subtract(6, "month");
        toDate = moment();
      }
      if (range === "CUSTOM") {
        if (formDetails.dateRange !== undefined) {
          fromDate = formDetails.dateRange[0].format("YYYY-MM-DD");
          toDate = formDetails.dateRange[1].format("YYYY-MM-DD");
        }
      }
      const reqParams = {
        accountNo: formDetails.accountNo,
        branchId: formDetails.branchId,
        fromDate: fromDate,
        toDate: toDate,
        txnsTypes: formDetails.txnsTypes,
      };
      const response = await PendingAuthService.loanstatementDownload(
        reqParams
      );
      if (response.status === 200) {
        setPwdSuccessVisible(true);
      }
    } catch (error) {}
  }, [range, formDetails]);

  return (
    <>
      <Card size="small" className="mb-2 breadcrumb-card">
        <Breadcrumb separator=">">
          {/* <Breadcrumb.Item>
            <span onClick={() => navigate("/dashboard")}>
              {t("navigation.home", {
                defaultValue: "Home",
              })}
            </span>
          </Breadcrumb.Item> */}
          <Breadcrumb.Item>
            <span onClick={() => navigate("/accounts")}>
              {t("navigation.account", {
                defaultValue: "Accounts",
              })}
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span>
              {" "}
              {t("pages.my_accounts.loans.loan_statement", {
                defaultValue: "Loan Statement",
              })}
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </Card>

      <Card size="small" className="mb-1 text-center breadcrumb-title-card">
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 d-flex justify-content-left align-items-center heading mx-0">
            <TitleIcon height={30} style={{ marginLeft: "-20px" }} />
            <h3 className="title mb-0">
              {" "}
              {t("pages.my_accounts.loans.loan_statement", {
                defaultValue: "Loan Statement",
              })}
            </h3>
          </div>
        </div>
      </Card>

      <Card className="account-tab-card mb-1">
        <Tabs
          defaultActiveKey="1"
          type="line"
          onChange={setActiveKey}
          activeKey={activeKey}
          onTabClick={handleTabClick}
        >
          <Tabs.TabPane
            tab={t("pages.my_accounts.loans.loan_and_finance_details", {
              defaultValue: "Loan and Finance Details",
            })}
            key="ACCOUNT_DETAILS"
          ></Tabs.TabPane>
          <Tabs.TabPane
            tab={t("buttons.view_stmt_btn", {
              defaultValue: "View Statement",
            })}
            key="DETAILED_STATEMENT"
          ></Tabs.TabPane>
          {/* {["I", "AA"].indexOf(mode) > -1 && (
            <Tabs.TabPane
              tab={t("pages.my_accounts.loans.repayment", {
                defaultValue: "Repayment",
              })}
              key="REPAYMENT"
            ></Tabs.TabPane>
          )}
          <Tabs.TabPane
            tab={t("pages.my_accounts.loans.disbursement_inquiry", {
              defaultValue: "Disbursement Inquiry",
            })}
            key="DISBURSEMENT_INQUIRY"
          ></Tabs.TabPane>
          <Tabs.TabPane
            tab={t("pages.my_accounts.loans.schedule_inquiry", {
              defaultValue: "Schedule Inquiry",
            })}
            key="SCHEDULE_INQUIRY"
          ></Tabs.TabPane> */}
        </Tabs>
      </Card>

      <Card className="application-card">
        <div className="d-flex justify-content-between">
          <div className="d-flex">
            <div className="account-details me-4 hide-xs">
              <label>
                {" "}
                {t("pages.accounts.accounts_common.account_no", {
                  defaultValue: "Account Number",
                })}
              </label>
              <span>{accountDetails?.accountNo}</span>
            </div>
            <div>
              {" "}
              <Form
                layout="inline"
                form={form}
                onFinish={getLoanStatement}
                initialValues={formValues}
              >
                {" "}
                <Form.Item
                  name="dateType"
                  label=""
                  className="deposit-width my-2"
                >
                  <Select
                    bordered={true}
                    size="large"
                    onSelect={(e) => setRange(e)}
                  >
                    {PeriodTypes.map((option, idx) => (
                      <Select.Option key={idx} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                {range === "CUSTOM" && (
                  <Form.Item label="" name="dateRange" className="my-2">
                    <DatePicker.RangePicker
                      disabledDate={(current) => {
                        return (
                          current &&
                          (current.isAfter(moment().endOf("day")) ||
                            current.isBefore(
                              moment().subtract(6, "months").startOf("day")
                            ))
                        );
                      }}
                      format={"DD-MM-YYYY"}
                      style={{ width: "100%" }}
                      size="large"
                    />
                  </Form.Item>
                )}{" "}
                <Form.Item
                  name="txnsTypes"
                  label=""
                  className="deposit-width my-2"
                >
                  <Select bordered={true} size="large">
                    {txnTypes.map((option, idx) => (
                      <Select.Option key={idx} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Button
                  type="primary"
                  className="primary-btn mt-2"
                  size="large"
                  htmlType="submit"
                >
                  {t("pages.my_accounts.deposits.apply_filter", {
                    defaultValue: "Apply Filter",
                  })}
                </Button>
                <Button
                  className="secondary-btn mx-3 mt-2"
                  size="large"
                  onClick={handleClear}
                >
                  {t("buttons.clear_btn", {
                    defaultValue: "Clear",
                  })}
                </Button>
                {dataSource.length > 0 && (
                  <div className="d-flex justify-content-end mt-2">
                    <Button
                      size="large"
                      type="primary"
                      className="primary-btn"
                      onClick={() => handleDownload()}
                    >
                      {t("buttons.download_btn", { defaultValue: "Download" })}
                    </Button>
                  </div>
                )}
              </Form>
            </div>{" "}
          </div>
        </div>
      </Card>

      <Card className="application-card mt-1" style={{ padding: "5px" }}>
        {showGridAccount === "GRID" && (
          <LoanStatementGrid dataSource={dataSource} />
        )}
        {showGridAccount === "LIST" && (
          <LoanStatementList dataSource={dataSource} />
        )}
      </Card>

      <Modal
        title={""}
        open={pwdSuccessVisible}
        footer={null}
        width={480}
        closable={false}
        maskClosable={false}
        mask={true}
      >
        <div
          style={{ width: 330, margin: "0 auto" }}
          align="center"
          className="mt-2"
        >
          <AiOutlineInfoCircle size={55} className="primary-color" />
          <h6
            className="auth-header-label"
            style={{ textTransform: "initial" }}
          >
            {t("pages.my_accounts.casa.pdf_pwd_txt", {
              defaultValue:
                "The PDF is secured with a password, which is a combination of the initial eight digits of your account number.",
            })}
          </h6>
          <div className="mt-4 mb-3" align="center">
            <Button
              type="primary"
              className="btn-primary"
              style={{ width: 120 }}
              size="large"
              onClick={() => {
                setPwdSuccessVisible(false);
              }}
            >
              {t("buttons.okay_btn", {
                defaultValue: "Okay",
              })}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
